
import { useState } from "react";
import { LensInventory } from "@/types/inventory";
import { LensCard } from "@/components/inventory/LensCard";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Glasses, ChevronUp, ChevronDown } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

interface CompatibleLensesSectionProps {
  isLensesOpen: boolean;
  setIsLensesOpen: (value: boolean) => void;
  compatibleLenses: LensInventory[];
  cameraMount: string;
  hasAdapter: boolean;
  adapterMounts: string[];
  formatMount: (mount: string) => string;
  handleLensUpdate: () => void;
}

export function CompatibleLensesSection({
  isLensesOpen,
  setIsLensesOpen,
  compatibleLenses,
  cameraMount,
  hasAdapter,
  adapterMounts,
  formatMount,
  handleLensUpdate
}: CompatibleLensesSectionProps) {
  const { t } = useTranslation();

  return (
    <Collapsible
      open={isLensesOpen}
      onOpenChange={setIsLensesOpen}
      className="border rounded-lg overflow-hidden"
    >
      <div className="bg-card p-4 flex items-center justify-between border-b">
        <div>
          <h2 className="text-xl font-semibold">{t('compatible_lenses')}</h2>
          <p className="text-muted-foreground text-sm">
            {t('lenses')} that can be used with this camera
          </p>
        </div>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            {isLensesOpen ? 
              <ChevronUp className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
            }
          </Button>
        </CollapsibleTrigger>
      </div>
      
      <CollapsibleContent>
        <div className="p-4">
          {compatibleLenses.length === 0 ? (
            <div className="text-center py-12">
              <div className="flex flex-col items-center justify-center space-y-3">
                <Glasses className="h-12 w-12 text-muted-foreground opacity-20" />
                <h3 className="font-medium">{t('no_lenses_found')}</h3>
                <p className="text-sm text-muted-foreground">
                  {hasAdapter
                    ? `${t('no_lenses_yet')} ${t('with')} ${formatMount(cameraMount)} ${t('or')} ${adapterMounts.map(formatMount).join(', ')} ${t('mount')}`
                    : `${t('no_lenses_yet')} ${t('with')} ${formatMount(cameraMount)} ${t('mount')}`}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {compatibleLenses.map(lens => (
                <LensCard 
                  key={lens.id} 
                  lens={lens} 
                  onActionComplete={handleLensUpdate} 
                />
              ))}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
